import {
  multiFactor,
  TotpMultiFactorGenerator,
  TotpSecret,
  type MultiFactorInfo,
} from 'firebase/auth';
import QRCode from 'qrcode';
import { firebaseAuth } from './firebase';

/**
 * TOTP (authenticator-app) multi-factor enrollment, on top of the Firebase
 * SDK. The SDK's `multiFactor(user).enrolledFactors` is the source of truth
 * for whether MFA is on — it reflects enroll/unenroll immediately, before the
 * server-side `mfa_enrolled_at` flag catches up on the next MFA sign-in.
 *
 * Requires the project to be on Identity Platform with TOTP MFA enabled
 * (a one-time project setting). Until then enroll() rejects with
 * `auth/operation-not-allowed`, which the UI surfaces.
 */

const ISSUER = 'KEOBOOKS';

export type TotpEnrollment = {
  secret: TotpSecret;
  /** otpauth:// URI encoded as a PNG data URL for an <img> QR code. */
  qrDataUrl: string;
  /** Base32 shared key for manual entry when a camera isn't available. */
  sharedKey: string;
};

export type EnrolledFactor = {
  uid: string;
  displayName: string | null;
  enrollmentTime: string;
};

function requireUser() {
  const user = firebaseAuth?.currentUser;
  if (!user) throw new Error('You must be signed in to manage two-factor authentication.');
  return user;
}

/** Whether the signed-in user has at least one TOTP factor enrolled. */
export function totpFactors(): EnrolledFactor[] {
  const user = firebaseAuth?.currentUser;
  if (!user) return [];
  return multiFactor(user)
    .enrolledFactors.filter(
      (f: MultiFactorInfo) => f.factorId === TotpMultiFactorGenerator.FACTOR_ID,
    )
    .map((f: MultiFactorInfo) => ({
      uid: f.uid,
      displayName: f.displayName ?? null,
      enrollmentTime: f.enrollmentTime,
    }));
}

/** Begin enrollment: generate a secret and a scannable QR for the auth app. */
export async function startTotpEnrollment(accountLabel: string): Promise<TotpEnrollment> {
  const user = requireUser();
  const session = await multiFactor(user).getSession();
  const secret = await TotpMultiFactorGenerator.generateSecret(session);
  const otpauthUri = secret.generateQrCodeUrl(accountLabel, ISSUER);
  const qrDataUrl = await QRCode.toDataURL(otpauthUri, { margin: 1, width: 220 });
  return { secret, qrDataUrl, sharedKey: secret.secretKey };
}

/** Finish enrollment by verifying a 6-digit code from the authenticator app. */
export async function finishTotpEnrollment(
  secret: TotpSecret,
  code: string,
  displayName = 'Authenticator app',
): Promise<void> {
  const user = requireUser();
  const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, code.trim());
  await multiFactor(user).enroll(assertion, displayName);
  // Refresh so the API sees the updated factor state on the next request.
  await user.getIdToken(true);
}

/** Remove an enrolled factor by its uid. Requires a recent sign-in. */
export async function unenrollTotp(uid: string): Promise<void> {
  const user = requireUser();
  await multiFactor(user).unenroll(uid);
  await user.getIdToken(true);
}
